import type { CommandResult, HomeCommand, HomeState } from "@home-control/shared";

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? "/api";

export class AuthenticationError extends Error {
  constructor(message = "Authentication failed") {
    super(message);
    this.name = "AuthenticationError";
  }
}

export class NotFoundError extends Error {
  constructor(message = "Not found") {
    super(message);
    this.name = "NotFoundError";
  }
}

export async function login(password: string): Promise<string> {
  const response = await fetch(`${apiBaseUrl}/auth/login`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ password })
  });
  if (!response.ok) {
    throw new Error("Login failed");
  }
  const body = (await response.json()) as { token: string };
  return body.token;
}

export async function fetchState(token: string): Promise<HomeState | null> {
  const response = await fetch(`${apiBaseUrl}/state`, {
    headers: { authorization: `Bearer ${token}` }
  });
  if (response.status === 401) {
    throw new AuthenticationError("Session expired");
  }
  if (!response.ok) {
    throw new Error("State read failed");
  }
  const body = (await response.json()) as { state: HomeState | null };
  return body.state;
}

export type HistoryRange = "24h" | "7d";
export type ClimateHistoryZone = "hallway" | "bedroom" | "mini-split";

export interface ClimateHistoryPoint {
  timestamp: string;
  temperatureF: number | null;
  targetTemperatureF: number | null;
  coolingOn: number;
  heatingOn: number;
  runtimeOn: number;
}

export interface ClimateHistory {
  zone: ClimateHistoryZone;
  periodSeconds: number;
  points: ClimateHistoryPoint[];
}

export interface PoolHistoryPoint {
  timestamp: string;
  temperatureF: number | null;
  pumpOn: number | null;
  heaterOn: number | null;
}

export interface PoolHistory {
  range: HistoryRange;
  periodSeconds: number;
  points: PoolHistoryPoint[];
}

async function fetchHistoryResource<T>(token: string, path: string): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    headers: { authorization: `Bearer ${token}` }
  });
  if (response.status === 401) {
    throw new AuthenticationError("Session expired");
  }
  if (response.status === 404) {
    throw new NotFoundError();
  }
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, "History read failed"));
  }
  const body = (await response.json()) as { history: T };
  return body.history;
}

async function readErrorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string };
    return body.error || fallback;
  } catch {
    return fallback;
  }
}

export async function fetchClimateHistory(
  token: string,
  zone: ClimateHistoryZone,
  range: HistoryRange
): Promise<ClimateHistory> {
  return fetchHistoryResource(token, `/history?zone=${encodeURIComponent(zone)}&range=${range}`);
}

export async function fetchPoolHistory(
  token: string,
  range: HistoryRange,
  from: string,
  to: string
): Promise<PoolHistory> {
  const params = new URLSearchParams({ range, from, to });
  return fetchHistoryResource(token, `/pool/history?${params.toString()}`);
}

export async function sendCommand(token: string, command: HomeCommand): Promise<string> {
  const response = await fetch(`${apiBaseUrl}/command`, {
    method: "POST",
    headers: {
      authorization: `Bearer ${token}`,
      "content-type": "application/json"
    },
    body: JSON.stringify(command)
  });
  if (response.status === 401) {
    throw new AuthenticationError("Session expired");
  }
  if (!response.ok) {
    throw new Error("Command failed");
  }
  const body = (await response.json()) as { command: { id: string } };
  return body.command.id;
}

export async function fetchCommandResult(token: string, id: string): Promise<CommandResult | null> {
  const response = await fetch(`${apiBaseUrl}/command/${encodeURIComponent(id)}`, {
    headers: { authorization: `Bearer ${token}` }
  });
  if (response.status === 401) {
    throw new AuthenticationError("Session expired");
  }
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error("Command result read failed");
  }
  const body = (await response.json()) as { result: CommandResult | null };
  return body.result;
}
